import './DetectedAppNotice.scss';

import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { getQueryString } from '@sonar-software/sonar-http';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import React from 'react';
import { SonarText } from '@ui/text';
import { Space } from 'antd';

import {
	COPY_SPECIFIC,
	DATA_SECURITY_DASHBOARD,
	FULL_ROUTES,
	LINKS,
	ORGANIZATION_TYPE_LABELS
} from '@sonar-software/sonar-ts-constants';
import { Else, If, Then } from 'react-if';
import { FullUserBoundary, SanitizedHtml } from '@ui/general';
import { useApplicationReference, useAsyncStateWithPreload, useCurrentOrganizationSubdomain } from '@hooks';

function withColon(value: string): string {
	return `${value}:`;
}

const getDataSecurityActionPlanSelector = createAsyncSelectors((state) => state.core.dataSecurity.actionPlan.get);

export interface DetectedAppNoticeProps {
	applicationId: number;
}

export const DetectedAppNotice: React.FC<DetectedAppNoticeProps> = ({ applicationId }) => {
	const { name } = useApplicationReference(applicationId);
	const subdomain = useCurrentOrganizationSubdomain();

	const { data, isRequested } = useAsyncStateWithPreload(
		getDataSecurityActionPlanSelector,
		coreActions.requestGetCoreDataSecurityActionPlan,
		true,
		{ applicationId }
	);

	if (isRequested) {
		<LoadingSpinner
			spinning={ isRequested } />;
	}

	return (
		<If condition={ Boolean(data?.text) }>
			<Then>
				{ data && <SanitizedHtml html={ data.text } /> }
			</Then>
			<Else>
				<div className='detected-app-notice'>
					<Space
						direction='vertical'
						size='small'>
						<div className='h3'>{ DATA_SECURITY_DASHBOARD.SUGGESTED_ACTION_PLAN_DETECTED_APP }</div>
						<SonarText>{ DATA_SECURITY_DASHBOARD.DETECTED_APP_ACCESS_CHANGES(name) }</SonarText>
					</Space>
					<div className='sub-header'>
						{ withColon(DATA_SECURITY_DASHBOARD.PERMISSION_AND_ACCESS_CONTROL) }
					</div>
					<ul>
						<li>{ DATA_SECURITY_DASHBOARD.ACCESS_PERMISSIONS }
							<a
								href={ LINKS.SALESFORCE_MANAGE_USERS(subdomain) }
								target='_blank'>{ ORGANIZATION_TYPE_LABELS.SALESFORCE }
							</a>
						</li>
						<li>{ DATA_SECURITY_DASHBOARD.REVIEW_FIELD_LEVEL_SECURITY }
							<a
								href={ LINKS.SALESFORCE_MANAGE_OBJECTS(subdomain) }
								target='_blank'>{ COPY_SPECIFIC.SALESFORCE_OBJECT_MANAGER }
							</a>
						</li>
					</ul>
					<div className='sub-header'>
						{ withColon(DATA_SECURITY_DASHBOARD.SECURITY_AND_COMPLIANCE_REVIEW) }
					</div>
					<ul>
						<li>{ DATA_SECURITY_DASHBOARD.AUDIT_TRAIL }</li>
						<li>{ DATA_SECURITY_DASHBOARD.DATA_SECURITY_POLICIES }</li>
						<li>{ DATA_SECURITY_DASHBOARD.THIRD_PARTY_ASSESSMENT(name) }</li>
					</ul>
					<FullUserBoundary>
						<div className='sub-header'>
							{ withColon(DATA_SECURITY_DASHBOARD.MONITORING_AND_INCIDENT_MANAGEMENT) }
						</div>
						<ul>
							<li>
								<Link to={ `${FULL_ROUTES.ADD_APP}?${getQueryString({ applicationId })}` }>
									{ withColon(DATA_SECURITY_DASHBOARD.MONITOR_APP(name)) }
								</Link> { DATA_SECURITY_DASHBOARD.ADD_TO_MONITOR }
							</li>
						</ul>
					</FullUserBoundary>
				</div>
			</Else>
		</If>);
};
